import { supabase } from '@/core/database/supabase';
import type { 
  MaintenanceIssue, 
  IssueComment, 
  IssueStats, 
  IssueFilters 
} from '../types/maintenance-issues.types';

export const MaintenanceIssuesService = {
  /**
   * Lấy danh sách sự cố
   */
  async getIssues(companyId: string, filters: IssueFilters = {}): Promise<{ data: MaintenanceIssue[], total: number }> {
    let query = supabase
      .from('maintenance_issues')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, zone_name),
        reported_by_user:user_profiles!reported_by(id, full_name, avatar_url:avatar_path),
        assignee:user_profiles!assigned_to(id, full_name, avatar_url:avatar_path),
        team:teams(id, name),
        work_order:work_orders(id, work_order_code, title)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.severity && filters.severity !== 'all') query = query.eq('severity', filters.severity);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    if (filters.assignedTo) query = query.eq('assigned_to', filters.assignedTo);
    if (filters.search) {
      query = query.or(`title.ilike.%${filters.search}%,issue_code.ilike.%${filters.search}%`);
    }

    query = query.order('created_at', { ascending: false });

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as MaintenanceIssue[],
      total: count || 0
    };
  },

  /**
   * Chi tiết sự cố
   */
  async getIssueDetail(id: string): Promise<MaintenanceIssue> {
    const { data, error } = await supabase
      .from('maintenance_issues')
      .select(`
        *,
        project:projects(id, name),
        zone:project_zones(id, zone_name),
        reported_by_user:user_profiles!reported_by(id, full_name, avatar_url:avatar_path),
        assignee:user_profiles!assigned_to(id, full_name, avatar_url:avatar_path),
        team:teams(id, name),
        work_order:work_orders(id, work_order_code, title)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data as MaintenanceIssue;
  },

  /**
   * Thống kê sự cố
   */
  async getIssueStats(companyId: string): Promise<IssueStats> {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('maintenance_issues')
      .select('status_code, is_sla_breach, resolved_at')
      .eq('company_id', companyId);

    if (error) throw error;

    return {
      openIssues: data.filter(i => i.status_code === 'open').length,
      inProgress: data.filter(i => i.status_code === 'in_progress').length,
      slaBreaches: data.filter(i => i.is_sla_breach).length,
      resolvedToday: data.filter(i => i.resolved_at?.split('T')[0] === today).length,
    };
  },

  /**
   * CRUD & Actions
   */
  async createIssue(data: Partial<MaintenanceIssue>): Promise<MaintenanceIssue> {
    const { data: result, error } = await supabase
      .from('maintenance_issues')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as MaintenanceIssue;
  },

  async updateIssue(id: string, updates: Partial<MaintenanceIssue>): Promise<MaintenanceIssue> {
    const { data, error } = await supabase
      .from('maintenance_issues')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as MaintenanceIssue;
  },

  /**
   * Phân công xử lý
   */
  async assignIssue(id: string, userId: string, teamId?: string): Promise<void> {
    const { error } = await supabase
      .from('maintenance_issues')
      .update({
        assigned_to: userId,
        assigned_team_id: teamId,
        status_code: 'assigned',
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) throw error;
  }
};

